import React, { useState, useEffect } from 'react';
import { ScatterChart, Scatter, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { dashboardAPI } from '../services/api';
import StressGauge from '../components/StressGauge';

const RISK_CONFIG = {
  Low:      { color: '#10b981', glow: 'shadow-emerald-500/30', border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', emoji: '✅' },
  Mild:     { color: '#3b82f6', glow: 'shadow-blue-500/30',    border: 'border-blue-500/30',    bg: 'bg-blue-500/10',    emoji: '🔵' },
  Moderate: { color: '#f59e0b', glow: 'shadow-yellow-500/30',  border: 'border-yellow-500/30',  bg: 'bg-yellow-500/10',  emoji: '⚠️' },
  High:     { color: '#f97316', glow: 'shadow-orange-500/30',  border: 'border-orange-500/30',  bg: 'bg-orange-500/10',  emoji: '🔴' },
  Critical: { color: '#ef4444', glow: 'shadow-red-500/30',     border: 'border-red-500/30',     bg: 'bg-red-500/10',     emoji: '🚨' },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-xl p-3 shadow-2xl">
      <p className="text-slate-400 text-xs mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color }}>{p.name}: {p.value?.toFixed ? p.value.toFixed(1) : p.value}</p>
      ))}
    </div>
  );
};

function StatCard({ icon, label, value, unit, color, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={`rounded-2xl p-5 border transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ background: `${color}12`, borderColor: `${color}35` }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{icon}</span>
        <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="text-4xl font-black text-white">{value}<span className="text-lg font-normal text-slate-400 ml-1">{unit}</span></div>
    </div>
  );
}

export default function Analysis() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [s, c] = await Promise.all([dashboardAPI.getStats(), dashboardAPI.getCharts()]);
      setStats(s.data);
      setCharts(c.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-6">
          {[0,1,2].map(i => <div key={i} className="absolute inset-0 rounded-full border-2 border-blue-500/40 animate-ping" style={{ animationDelay: `${i*0.2}s` }}/>)}
          <div className="absolute inset-0 flex items-center justify-center text-4xl">📊</div>
        </div>
        <p className="text-slate-400 text-lg font-medium">Loading Analysis...</p>
      </div>
    </div>
  );

  if (!stats || stats.message) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center bg-slate-800/60 border border-slate-700 rounded-3xl p-12">
        <div className="text-7xl mb-4">🧬</div>
        <h2 className="text-2xl font-bold text-white mb-2">No Data Yet</h2>
        <p className="text-slate-400">Make a prediction first to see your analysis</p>
        <a href="/prediction" className="mt-6 inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-transform">Go to Prediction →</a>
      </div>
    </div>
  );

  const cfg = RISK_CONFIG[stats.risk_category] || RISK_CONFIG.Low;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="text-center py-6 sm:py-10 px-4">
        <div className="inline-flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-2xl px-4 py-2 mb-3">
          <span className="text-lg sm:text-2xl">📊</span>
          <span className="text-xs sm:text-sm font-semibold text-slate-300 tracking-widest uppercase">Real-time Analysis</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-black bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">Analysis</h1>
        <p className="text-slate-400 text-sm sm:text-lg">Live biometric stress analysis & sleep intelligence</p>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-16 space-y-4 sm:space-y-6">

        {/* Top Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Gauge Card */}
          <div className={`bg-slate-800/60 border ${cfg.border} rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl flex flex-col items-center justify-center`}>
            <div className="text-xs text-slate-400 uppercase tracking-widest font-semibold mb-2">Current Stress</div>
            <div className="bg-black/40 rounded-2xl p-2 w-full flex justify-center">
              <StressGauge score={stats.current_stress_score} />
            </div>
            <div className={`mt-3 px-4 py-1.5 rounded-full text-base sm:text-lg font-black ${cfg.bg} border ${cfg.border}`} style={{ color: cfg.color }}>
              {cfg.emoji} {stats.risk_category} Risk
            </div>
          </div>

          {/* Stats */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-3 sm:gap-4">
            <StatCard icon="⚡" label="Stress Level"  value={`${stats.current_stress_level}/4`} unit=""     color="#ef4444" delay={100}/>
            <StatCard icon="🌙" label="Avg Sleep"     value={stats.avg_sleep_hours}              unit="hrs"  color="#3b82f6" delay={200}/>
            <StatCard icon="📈" label="Avg Stress"    value={stats.avg_stress_score}             unit="/100" color="#f59e0b" delay={300}/>
            <StatCard icon="🗂️" label="Total Records" value={stats.total_records}                unit=""     color="#8b5cf6" delay={400}/>
          </div>
        </div>

        {/* Burnout Alert */}
        {stats.burnout_risk && (
          <div className="bg-red-500/10 border-2 border-red-500/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex items-center gap-3 sm:gap-5 animate-pulse">
            <div className="text-3xl sm:text-5xl">🚨</div>
            <div>
              <div className="text-lg sm:text-2xl font-black text-red-400">BURNOUT ALERT</div>
              <div className="text-red-300/80 mt-1 text-xs sm:text-base">Sleep under 5 hours for 3 consecutive days.</div>
            </div>
          </div>
        )}

        {charts && (
          <>
            {/* Weekly Trend */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">📈</div>
                <div>
                  <h3 className="font-bold text-white text-lg">Weekly Stress & Sleep Trend</h3>
                  <p className="text-slate-400 text-xs">Last 7 days biometric overview</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={charts.weekly_trend}>
                  <defs>
                    <linearGradient id="stressGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="sleepGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                  <XAxis dataKey="date" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                  <YAxis stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                  <Tooltip content={<CustomTooltip/>}/>
                  <Legend wrapperStyle={{ color: '#94a3b8' }}/>
                  <Area type="monotone" dataKey="stress" stroke="#ef4444" strokeWidth={2.5} fill="url(#stressGrad)" name="Stress"/>
                  <Area type="monotone" dataKey="sleep"  stroke="#3b82f6" strokeWidth={2.5} fill="url(#sleepGrad)"  name="Sleep"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Bottom Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">💤</div>
                  <div>
                    <h3 className="font-bold text-white">Sleep vs Stress</h3>
                    <p className="text-slate-400 text-xs">Correlation scatter plot</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                    <XAxis dataKey="sleep" name="Sleep Hours" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} label={{ value: 'Sleep (hrs)', fill: '#64748b', fontSize: 11, position: 'insideBottom', offset: -2 }}/>
                    <YAxis dataKey="stress" name="Stress Score" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                    <Tooltip content={<CustomTooltip/>} cursor={{ strokeDasharray: '3 3', stroke: '#475569' }}/>
                    <Scatter data={charts.sleep_vs_stress} fill="#8b5cf6" opacity={0.8}/>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">❤️</div>
                  <div>
                    <h3 className="font-bold text-white">Heart Rate vs Stress</h3>
                    <p className="text-slate-400 text-xs">Top 10 records</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={charts.hr_vs_stress.slice(0, 10)}>
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316"/>
                        <stop offset="100%" stopColor="#ef4444"/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                    <XAxis dataKey="hr" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                    <YAxis stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar dataKey="stress" fill="url(#barGrad)" radius={[6,6,0,0]} name="Stress"/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
